import type { AcquisitionReportPage, AcquisitionReportRow, FinanceEventRow } from '../api/amazonconnect';
import { calendarDay } from './ordersTruth';

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim()) {
    const n = Number(v.replace(/,/g, ''));
    if (Number.isFinite(n)) return n;
  }
  const nested = asRecord(v);
  if (nested && 'amount' in nested) return num(nested.amount);
  return null;
}

/** Read Amazon cells including nested salesByAsin / trafficByAsin and dotted flatten keys. */
export function payloadNum(payload: Record<string, unknown> | undefined, keys: string[]): number | null {
  if (!payload) return null;
  const nests = ['trafficByAsin', 'salesByAsin', 'traffic', 'sales'];
  for (const k of keys) {
    const direct = num(payload[k]);
    if (direct != null) return direct;
    for (const nest of nests) {
      const dotted = num(payload[`${nest}.${k}`]);
      if (dotted != null) return dotted;
    }
  }
  for (const nest of nests) {
    const inner = asRecord(payload[nest]);
    if (!inner) continue;
    for (const k of keys) {
      const v = num(inner[k]);
      if (v != null) return v;
    }
    const ops = asRecord(inner.orderedProductSales);
    const amt = ops ? num(ops.amount) : null;
    if (amt != null && keys.includes('orderedProductSales')) return amt;
  }
  return null;
}

export function payloadStr(payload: Record<string, unknown> | undefined, keys: string[]): string {
  if (!payload) return '';
  for (const k of keys) {
    const v = payload[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

export function sumPayload(page: AcquisitionReportPage | undefined, keys: string[]): number | null {
  const items = page?.items ?? [];
  if (!items.length) return null;
  let s = 0;
  let n = 0;
  for (const row of items) {
    const v = payloadNum(row.payload, keys);
    if (v == null) continue;
    s += v;
    n += 1;
  }
  return n === 0 ? null : s;
}

export function fbaFacts(page: AcquisitionReportPage | undefined) {
  return {
    totalRows: page?.total_count ?? 0,
    sample: page?.items.length ?? 0,
    truncated: Boolean(page?.has_more),
    fulfillable: sumPayload(page, ['afn-fulfillable-quantity', 'afn-warehouse-quantity']),
    inbound: sumPayload(page, ['afn-inbound-working', 'afn-inbound-working-quantity']),
  };
}

export function returnsFacts(page: AcquisitionReportPage | undefined) {
  const qtyKeys = ['Return quantity', 'return-quantity', 'Quantity', 'quantity'];
  return {
    totalRows: page?.total_count ?? 0,
    sample: page?.items.length ?? 0,
    truncated: Boolean(page?.has_more),
    units: sumPayload(page, qtyKeys),
  };
}

/** Sparse day → return units from Return request date on the sample only. */
export function returnsByDay(
  page: AcquisitionReportPage | undefined,
  tz = 'Asia/Kolkata',
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const row of page?.items ?? []) {
    const raw = payloadStr(row.payload, [
      'Return request date',
      'return-request-date',
      'Return date',
      'return_date',
    ]);
    const ymd = raw.match(/(\d{4}-\d{2}-\d{2})/)?.[1];
    const iso = ymd ?? (Number.isFinite(Date.parse(raw)) ? calendarDay(raw, tz) : '');
    if (!iso || iso.includes('Invalid')) continue;
    const q = payloadNum(row.payload, ['Return quantity', 'return-quantity', 'Quantity', 'quantity']) ?? 1;
    out[iso] = (out[iso] ?? 0) + q;
  }
  return out;
}

export function trafficFacts(page: AcquisitionReportPage | undefined) {
  return {
    totalRows: page?.total_count ?? 0,
    sample: page?.items.length ?? 0,
    truncated: Boolean(page?.has_more),
    sessions: sumPayload(page, ['sessions']),
    pageViews: sumPayload(page, ['pageViews', 'page-views']),
    buyBoxPct: avgPayload(page, ['buyBoxPercentage', 'buy-box-percentage']),
  };
}

function avgPayload(page: AcquisitionReportPage | undefined, keys: string[]): number | null {
  const items = page?.items ?? [];
  let s = 0;
  let n = 0;
  for (const row of items) {
    const v = payloadNum(row.payload, keys);
    if (v == null) continue;
    s += v;
    n += 1;
  }
  return n === 0 ? null : s / n;
}

export function settlementFacts(page: AcquisitionReportPage | undefined) {
  return {
    totalRows: page?.total_count ?? 0,
    sample: page?.items.length ?? 0,
    truncated: Boolean(page?.has_more),
    amount: sumPayload(page, ['amount', 'Amount']),
  };
}

export function shipmentFacts(page: AcquisitionReportPage | undefined) {
  return {
    totalRows: page?.total_count ?? 0,
    sample: page?.items.length ?? 0,
    truncated: Boolean(page?.has_more),
  };
}

export function financeRollup(events: FinanceEventRow[]) {
  let refund = 0;
  let fee = 0;
  let other = 0;
  let n = 0;
  for (const e of events) {
    const a = Number(e.amount);
    if (!Number.isFinite(a)) continue;
    n += 1;
    const t = `${e.event_type ?? ''} ${e.transaction_type ?? ''} ${e.fee_type ?? ''}`.toLowerCase();
    if (t.includes('refund') || t.includes('return')) refund += a;
    else if (t.includes('fee') || t.includes('commission')) fee += a;
    else other += a;
  }
  return { refund, fee, other, withAmount: n };
}

export function reportRows(page: AcquisitionReportPage | undefined): AcquisitionReportRow[] {
  return page?.items ?? [];
}
